import React, { useState } from 'react';
import { supabase } from '../../../services/supabaseClient';

const EditLanguagesModal = ({ submissionId, languageData, onClose, onSave }) => {
    const [formData, setFormData] = useState(languageData || {});
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('wizard_submissions')
                .update({ language_info: formData })
                .eq('id', submissionId);

            if (error) throw error;
            if (onSave) await onSave();
            onClose();
        } catch (err) {
            console.error('Error saving languages:', err);
            alert('Failed to save language information');
        } finally {
            setSaving(false);
        }
    };

    const updateIELTSScore = (field, value) => {
        setFormData({
            ...formData,
            ieltsScores: {
                ...formData.ieltsScores,
                [field]: value
            }
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between sticky top-0">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">Edit Language Skills</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">English Test</label>
                        <select
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                            value={formData.englishTest || ''}
                            onChange={(e) => setFormData({ ...formData, englishTest: e.target.value })}
                        >
                            <option value="">Select test</option>
                            <option value="IELTS">IELTS</option>
                            <option value="TOEFL">TOEFL</option>
                            <option value="PTE">PTE</option>
                            <option value="None">None</option>
                        </select>
                    </div>

                    {formData.englishTest === 'IELTS' && (
                        <div className="border border-gray-600 rounded p-4 bg-gray-900/50">
                            <h4 className="text-sm font-bold text-white mb-3">IELTS Scores</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Overall</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="9"
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                        value={formData.ieltsScores?.overall || ''}
                                        onChange={(e) => updateIELTSScore('overall', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Listening</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="9"
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                        value={formData.ieltsScores?.listening || ''}
                                        onChange={(e) => updateIELTSScore('listening', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Reading</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="9"
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                        value={formData.ieltsScores?.reading || ''}
                                        onChange={(e) => updateIELTSScore('reading', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Writing</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="9"
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                        value={formData.ieltsScores?.writing || ''}
                                        onChange={(e) => updateIELTSScore('writing', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Speaking</label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="9"
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                        value={formData.ieltsScores?.speaking || ''}
                                        onChange={(e) => updateIELTSScore('speaking', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {formData.englishTest === 'TOEFL' && (
                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">TOEFL Score</label>
                            <input
                                type="number"
                                min="0"
                                max="120"
                                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                value={formData.toeflScore || ''}
                                onChange={(e) => setFormData({ ...formData, toeflScore: e.target.value })}
                            />
                        </div>
                    )}

                    {formData.englishTest === 'PTE' && (
                        <div>
                            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">PTE Score</label>
                            <input
                                type="number"
                                min="0"
                                max="90"
                                className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                                value={formData.pteScore || ''}
                                onChange={(e) => setFormData({ ...formData, pteScore: e.target.value })}
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Native Language</label>
                        <input
                            type="text"
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                            value={formData.nativeLanguage || ''}
                            onChange={(e) => setFormData({ ...formData, nativeLanguage: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Other Languages (comma-separated)</label>
                        <input
                            type="text"
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                            value={Array.isArray(formData.otherLanguages) ? formData.otherLanguages.join(', ') : ''}
                            onChange={(e) => setFormData({ ...formData, otherLanguages: e.target.value.split(',').map(l => l.trim()).filter(Boolean) })}
                            placeholder="e.g., Spanish, French, German"
                        />
                    </div>

                    <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded text-sm font-bold bg-gray-700 text-white hover:bg-gray-600 border border-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 rounded text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 border border-blue-500 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditLanguagesModal;
